import { useState } from 'react';
import { observer } from 'mobx-react';
import { faFileArrowUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Drawer, message, Table, Upload, Divider } from 'antd';
import * as XLSX from 'xlsx';
import useStore from '../../../../store';
import { templeOriginalHeaderArr } from '../../../../config/Global';

const BulkImport = observer((props) => {
	const { visible, onClose } = props;
	const [fileList, setFileList] = useState([]);
	const [saving, setSaving] = useState(false);
	const [tableHeader, setTableHeader] = useState([]);
	const [tableData, setTableData] = useState([]);
	const { TEMPLE, BULKTEMPLE } = useStore();

	const checkFileHeaders = (fileHeader) => {
		if (templeOriginalHeaderArr.length !== fileHeader.length) {
			return false;
		}
		// Iterate through each element and compare
		for (let i = 0; i < fileHeader.length; i++) {
			// If elements at current index are not equal, arrays are not identical
			if (!templeOriginalHeaderArr.includes(fileHeader[i])) {
				return false;
			}
		}
		// If all elements are identical, arrays are identical
		return true;
	};

	const uploadProps = {
		name: 'file',
		multiple: false,
		maxCount: 1,
		accept: '.csv, .xlsx',
		beforeUpload: (file) => {
			const isCorrectFile =
				file.type === 'application/vnd.ms-excel' ||
				file.type ===
					'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
				file.type === 'text/csv';
			const isLt2M = file.size / 1024 / 1024 < 2;
			if (!isLt2M) {
				message.error('Excel file must be smaller than 2MB!');
			}
			if (!isCorrectFile) {
				setFileList([]); // Clear file list on incorrect file type
				message.error(`${file.name} is not excel file`);
				return false; // Stop further processing
			}
			setFileList([file]); // Clear previous files and set the new one
			const reader = new FileReader();
			reader.readAsArrayBuffer(file);
			reader.onload = (e) => {
				const workbook = XLSX.read(e.target.result, { type: 'buffer' });
				const data = XLSX.utils.sheet_to_json(
					workbook.Sheets[workbook.SheetNames[0]]
				);
				const headersData = XLSX.utils.sheet_to_json(
					workbook.Sheets[workbook.SheetNames[0]],
					{ header: 1 }
				);
				const tempHeader = [];
				if (data.length) {
					headersData[0].forEach((headerName) => {
						let tempObj = {
							title: headerName,
							dataIndex: headerName,
							render: (text) => {
								if (text && text.length > 100) {
									return <>{text.slice(0, 100)}...</>;
								} else {
									return <>{text}</>;
								}
							},
						};
						tempHeader.push(tempObj);
					});
				} else {
					setFileList([]); // Clear file list on incorrect file type
					message.error(
						`${file.name}'s has missing data. Please verify.`
					);
					return false; // Stop further processing
				}
				const headerverificationStatus = checkFileHeaders(
					headersData[0]
				);
				if (!headerverificationStatus) {
					setFileList([]); // Clear file list on incorrect file type
					message.error(
						`${file.name}'s headers not matched. Please cross verify your file.`
					);
					return false; // Stop further processing
				}

				setTableData(data.slice(0, 10));
				setTableHeader(tempHeader);
			};
			return false; // Prevent default upload behavior
		},
		onRemove: (file) => {
			const newFileList = fileList.filter(
				(item) => item.uid !== file.uid
			);
			setFileList(newFileList);
		},
	};

	const handleUpload = async () => {
		setSaving(true);
		let formData = new FormData();
		formData.append('file', fileList[0]);
		TEMPLE.bulkImport(formData);
		closeModal();
		if (BULKTEMPLE.agGrid) {
			BULKTEMPLE.setupGrid(BULKTEMPLE.agGrid);
		}
	};

	const closeModal = () => {
		setTableData([]);
		setTableHeader([]);
		setSaving(false);
		setFileList([]);
		onClose();
	};

	return (
		<Drawer
			title="Bulk Temples Import"
			onClose={closeModal}
			open={visible}
			destroyOnClose
			width={'60%'}
			className="uploadFile"
			footer={
				<div>
					<Button
						type="primary"
						key={1}
						loading={saving}
						onClick={handleUpload}
						disabled={!fileList.length}
					>
						Upload
					</Button>
					<Button
						key={2}
						onClick={closeModal}
						style={{ marginLeft: '15px' }}
					>
						Cancel
					</Button>
				</div>
			}
		>
			<div>
				<Upload
					{...uploadProps}
					fileList={fileList}
					onChange={() => {}}
					style={{ width: '100%' }}
				>
					<div className="upload-design">
						<p className="ant-upload-drag-icon">
							<FontAwesomeIcon icon={faFileArrowUp} size="3x" />
						</p>
						<p className="ant-upload-text">
							Click or drag file to this area to upload
						</p>
						<p className="ant-upload-hint">
							Support for a single file upload.
						</p>
					</div>
				</Upload>
				<Divider>Preview Data</Divider>
				<Table
					columns={tableHeader}
					dataSource={tableData}
					bordered
					pagination={false}
					style={{ marginTop: '20px' }}
				/>
			</div>
		</Drawer>
	);
});

export default BulkImport;
